import React from 'react'
import type { CalendarItem } from '@/types/contentPlanner'
import { cn } from '@/lib/utils'

interface CalendarDayCellProps {
  items: CalendarItem[]
  onDayClick: (items: CalendarItem[]) => void
  onItemClick?: (item: CalendarItem, event: React.MouseEvent) => void
  onGenerateItem?: (item: CalendarItem) => void
}

// Left-bar style: subtle bg + left color border
function getChipClasses(status: string): string {
  switch (status) {
    case 'published':
      return 'border-l-coral bg-coral-light text-coral-dark'
    case 'draft':
    case 'completed':
      return 'border-l-teal bg-teal-light text-teal-dark'
    case 'generating':
    case 'pending':
      return 'border-l-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400'
    case 'failed':
      return 'border-l-destructive bg-destructive/5 text-destructive'
    default:
      return 'border-l-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400'
  }
}

function truncateLabel(item: CalendarItem): string {
  const label = item.title || item.keyword || 'Untitled'
  return label.length > 28 ? label.slice(0, 27) + '…' : label
}

export function CalendarDayCell({ items, onDayClick, onItemClick, onGenerateItem }: CalendarDayCellProps) {
  if (items.length === 0) return null

  const visible = items.slice(0, 3)
  const overflow = items.length - 3

  return (
    <div
      className="flex flex-col gap-0.5 cursor-pointer w-full"
      onClick={() => onDayClick(items)}
    >
      {visible.map((item) => (
        <span
          key={item.id}
          /**
           * Names this chip for the View Transitions API so a
           * planner_set_calendar_date write MORPHS it from day A to day B
           * instead of one chip vanishing and an unrelated one appearing.
           *
           * Keyed on the content id, so the name follows the article across
           * day cells; a name derived from the date would break exactly when
           * the date changes, which is the only moment it is needed. The name
           * must be unique per document while a transition runs, and one
           * article renders once per grid, so the id satisfies that.
           */
          style={{ viewTransitionName: `webmcp-${item.id}` }}
          title={item.title || item.keyword || 'Untitled'}
          onClick={(event) => {
            if (!onItemClick) return
            event.stopPropagation()
            onItemClick(item, event)
          }}
          className={cn(
            'flex items-center gap-1 truncate rounded-r text-[10px] leading-tight font-medium',
            'border-l-2 pl-1.5 pr-1 py-0.5',
            onItemClick && 'hover:underline',
            getChipClasses(item.status),
          )}
        >
          {item._wsColor && (
            <span
              className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: item._wsColor }}
            />
          )}
          <span className="min-w-0 flex-1 truncate">{truncateLabel(item)}</span>
          {onGenerateItem && !['generating', 'in_progress'].includes(item.status) && (item.title || item.keyword) && (
            <button
              type="button"
              aria-label="Generate"
              className="shrink-0 rounded border border-current/25 bg-card/70 px-1 py-0.5 text-[9px] font-semibold leading-none hover:bg-card"
              onClick={(event) => {
                event.stopPropagation()
                onGenerateItem(item)
              }}
            >
              Generate
            </button>
          )}
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-[10px] text-muted-foreground pl-1">
          +{overflow} more
        </span>
      )}
    </div>
  )
}
